import React, { useEffect, useState } from 'react'
import { SubHeader } from '../../../components/SubHeader'
import { useLocation, useParams } from 'react-router-dom'
import { Button } from '../../../components/Button'
import api from '../../../services/api'
import { useTranslation } from 'react-i18next'
import { addBreadcrumb } from '../../../redux/reducers/breadcrumb/actions'
import { useAppDispatch } from '../../../redux/hooks'
import { DialogContent, DialogTitle, Stack } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import { Helmet } from 'react-helmet-async'
import { TrainingDataList } from './components/TrainingDataList'
import { InputSearch } from '../../../components/InputSearch'
import { TrainingForm } from './components/TrainingForm'
import { TreatEmpty } from 'components/TreatEmpty'
import { LoadingPage } from 'components/LoadingPage'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { Add } from '@mui/icons-material'
import { Training } from 'models/interfaces/training.interface'
import { TreatEmptySuggestion } from 'components/TreatEmptySuggestion'

export const TrainingList = () => {
  const dispatch = useAppDispatch()
  const { routineId } = useParams()
  const { t } = useTranslation()
  const location = useLocation()
  const [data, setData] = useState<Training[]>([])
  const [dataSearch, setDataSearch] = useState<Training[]>([])
  const [modalVisible, setModalVisible] = React.useState(false)
  const [training, setTraining] = useState<any>()
  const [loading, setLoading] = useState<boolean>(false)
  const [routine, setRoutine] = useState<[]>()
  const [trainingEmpty, setTrainingEmpty] = useState<boolean>(true)
  const [showInstruction, setShowInstruction] = useState<boolean>(false)

  useEffect(() => {
    dataFetch().catch(console.error)
    const body = document.querySelector('body')
    if (body) {
      body.style.overflow = modalVisible ? 'hidden' : 'auto'
    }
  }, [modalVisible])

  const dataFetch = async () => {
    dispatch(addBreadcrumb({ label: location.state?.name, path: location.pathname }))
    try {
      setLoading(true)
      const { data } = await api.get(`routines/${routineId}/trainings`)
      setTrainingEmpty(true)
      if (data) {
        setData(data)
        setDataSearch(data)
        setTrainingEmpty(false)
        setShowInstruction(data.length > 0 ? false : true)
        if (location.state) {
          setRoutine(location.state)
        } else {
          setRoutine(data[0].routine)
        }
      }
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = () => {
    setModalVisible(!modalVisible)
    setTraining(null)
  }

  const handleSearchField = (searchReturn: any) => {
    setDataSearch(searchReturn)
  }

  const handleEdit = (training: any) => {
    setTraining(training)
    setModalVisible(true)
  }

  const handleDelete = (training: Training[]) => {
    setTrainingEmpty(training.length > 0 ? false : true)
    setShowInstruction(training.length > 0 ? false : true)
  }

  return (
    <>
      <Helmet>
        <title> Training | DWF </title>
      </Helmet>
      <SubHeader
        goBack={false}
        onPress={handleOpenModal}
        path={location.pathname}
        titleButton={t<string>('training.newTraining')}
      />
      <Dialog
        open={modalVisible}
        onClose={() => {
          setModalVisible(false)
        }}
        fullWidth={true}>
        <DialogTitle sx={{ bgcolor: '#52525b', color: 'white' }}>
          { (training ? t('training.editTraining') : t('training.newTraining')) }
        </DialogTitle>
        <DialogContent dividers>
          <TrainingForm
            isVisible={modalVisible}
            training={training}
            routine={routine}
            onSubmitted={async () => {
              setModalVisible(false)
              await dataFetch()
            }}
            onCancel={() => setModalVisible(false)}
            loading={loading}/>
        </DialogContent>
      </Dialog >
      <>
        <Stack
          mb={{ xs: 2, sm: 0, md: 0 }}
          spacing={1}
          justifyContent="space-between"
          direction={{ xs: 'column', sm: 'row' }}
        >
          <Stack flex={1}>
            <InputSearch
              title={`${t('search.titleTraining')}`}
              data={data}
              params={['labels', 'name', 'note']}
              action={handleSearchField}/>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row', md: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center', md: 'center' }}
            spacing={2}
          >
            <Button
              title={`${t('training.newTraining')}`}
              startIcon={<Add />}
              onClick={handleOpenModal}
            />
          </Stack>
        </Stack>
        { loading
          ? <LoadingPage />
          : <>
            { trainingEmpty || data.length === 0
              ? <TreatEmptySuggestion
                textSuggestion={[
                  t('training.title.suggestion_1', { newTraining: t('training.newTraining') }),
                  t('training.title.suggestion_2')
                ]}
                showInstruction={showInstruction}
              />
              : dataSearch.length === 0
                ? <TreatEmpty subTitleMessage={t('general.searchEmpty')} />
                : <TrainingDataList items={dataSearch} onEdit={handleEdit} onDelete={handleDelete}/>
            }
          </>
        }
      </>
    </>
  )
}
